import { useCallback, useEffect } from "react";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { getGullakCashHistory } from "../services";
import type { CashHistoryRes } from "@/types/api/gullak-cash/cash-history";

type HistoryItem = NonNullable<CashHistoryRes['gulla_clash_history']>[0];

interface UseGullakCashHistoryOptions {
  operate: string;
  visible: boolean;
  pageSize?: number;
}

export function useGullakCashHistory({
  operate,
  visible,
  pageSize = 20,
}: UseGullakCashHistoryOptions) {
  const fetchData = useCallback(
    async (page: number, currentPageSize: number) => {
      try {
        const result = await getGullakCashHistory({
          operate,
          page: page.toString(),
          page_size: currentPageSize.toString(),
        });

        const list = result.gulla_clash_history || [];

        // 由于接口没有返回 total，我们需要判断是否还有更多数据
        // 如果返回的数据长度小于 pageSize，说明已经是最后一页
        const hasMore = list.length >= currentPageSize;

        // 模拟 total：已加载的数据数量 + (如果还有更多数据，则加1)
        const simulatedTotal = page * currentPageSize + (hasMore ? 1 : 0);

        return {
          list,
          total: simulatedTotal,
        };
      } catch (error) {
        console.error('Failed to fetch gullak cash history:', error);
        return {
          list: [],
          total: 0,
        };
      }
    },
    [operate]
  );

  const {
    data,
    loading,
    hasMore,
    scrollRef,
    resetAndFetch,
  } = useInfiniteScroll<HistoryItem>({
    fetchData,
    pageSize,
    enabled: visible,
  });

  useEffect(() => {
    if (visible) {
      resetAndFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, operate]);

  const isInitLoading = loading && data.length === 0;
  const isEmpty = !loading && data.length === 0;
  const isLoadingMore = loading && data.length > 0;

  return {
    data,
    loading,
    hasMore,
    scrollRef,
    isInitLoading,
    isEmpty,
    isLoadingMore,
  };
}
